<h2 mat-dialog-title class="dialog-title">
  {{ mode === 'add' ? 'Add New Load' : 'Update Load' }}
</h2>

<mat-dialog-content>
  <div class="template-row">
    <mat-form-field appearance="outline" [formGroup]="templateForm" class="template-field">
      <mat-label>Template</mat-label>

      <mat-select formControlName="id" (selectionChange)="onTemplateChange()">
        <mat-option [value]="null">None</mat-option>

        @for (template of templates; track template.id) {
          <mat-option [value]="template.id">
            {{ template.name }}
          </mat-option>
        }
      </mat-select>
    </mat-form-field>

    <div class="template-actions">
      <button mat-icon-button matTooltip="Add" type="button" (click)="createTemplate()">
        <mat-icon>add_circle_outline</mat-icon>
      </button>

      @if (templateForm.get('id')?.value) {
        <button mat-icon-button matTooltip="Copy" type="button" (click)="createTemplate('copy')">
          <mat-icon>content_copy</mat-icon>
        </button>

        <button mat-icon-button matTooltip="Update" type="button" (click)="updateTemplate()">
          <mat-icon>update</mat-icon>
        </button>

        <button mat-icon-button matTooltip="Delete" type="button" (click)="deleteTemplate()">
          <mat-icon>highlight_remove</mat-icon>
        </button>
      }
    </div>
  </div>

  <form [formGroup]="form" class="load-form">
    <div class="location-section">
      <div class="location-column">
        <mat-form-field appearance="outline" class="date-range-field">
          <mat-label>Origin date range</mat-label>

          <mat-date-range-input [rangePicker]="originPicker">
            <input matStartDate formControlName="origin_start_date" [min]="today" />
            <input matEndDate formControlName="origin_end_date" [min]="today" />
          </mat-date-range-input>

          <mat-datepicker-toggle matIconSuffix [for]="originPicker"></mat-datepicker-toggle>
          <mat-date-range-picker #originPicker></mat-date-range-picker>

          @if (form.get('origin_start_date')?.hasError('pastDate')) {
            <mat-error>Origin start date cannot be in the past</mat-error>
          } @else if (form.get('origin_start_date')?.invalid) {
            <mat-error>Invalid start date</mat-error>
          }

          @if (form.get('origin_end_date')?.hasError('pastDate')) {
            <mat-error>Origin end date cannot be in the past</mat-error>
          } @else if (form.get('origin_end_date')?.invalid) {
            <mat-error>Invalid end date</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline" class="location-field">
          <mat-label>Origin</mat-label>

          <input
            #originInput
            type="text"
            placeholder="Choose an origin"
            matInput
            formControlName="origin"
            [matAutocomplete]="pickAuto"
            (input)="filter('origin')"
            (focus)="filter('origin')"
          />

          <span matSuffix class="autocomplete-spinner">
            @if (loadingOrigin()) {
              <mat-spinner diameter="20" strokeWidth="3"></mat-spinner>
            }
          </span>

          <mat-autocomplete requireSelection #pickAuto="matAutocomplete">
            @for (option of filteredOriginOptions; track option.id) {
              <mat-option
                [value]="formatLocation(option)"
                (onSelectionChange)="onLocationSelected(option, 'origin')"
              >
                {{ formatLocation(option) }}
              </mat-option>
            }
          </mat-autocomplete>

          @if (form.get('origin')?.hasError('required') && form.get('origin')?.touched) {
            <mat-error>Origin is required</mat-error>
          }
        </mat-form-field>

      </div>

      <div class="location-column">
        <mat-form-field appearance="outline" class="date-range-field">
          <mat-label>Destination date range</mat-label>

          <mat-date-range-input [rangePicker]="destinationPicker">
            <input
              matStartDate
              formControlName="destination_start_date"
              placeholder="Start date"
              [min]="today"
            />

            <input
              matEndDate
              formControlName="destination_end_date"
              placeholder="End date"
              [min]="today"
            />
          </mat-date-range-input>

          <mat-datepicker-toggle matIconSuffix [for]="destinationPicker"></mat-datepicker-toggle>
          <mat-date-range-picker #destinationPicker></mat-date-range-picker>

          @if (form.get('destination_start_date')?.hasError('pastDate')) {
            <mat-error>Destination start date cannot be in the past</mat-error>
          } @else if (form.get('destination_start_date')?.invalid) {
            <mat-error>Invalid start date</mat-error>
          }

          @if (form.get('destination_end_date')?.hasError('pastDate')) {
            <mat-error>Destination end date cannot be in the past</mat-error>
          } @else if (form.get('destination_end_date')?.invalid) {
            <mat-error>Invalid end date</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline" class="location-field">
          <mat-label>Destination</mat-label>

          <input
            #destinationInput
            type="text"
            placeholder="Choose a destination"
            matInput
            formControlName="destination"
            [matAutocomplete]="deliveryAuto"
            (input)="filter('destination')"
            (focus)="filter('destination')"
          />

          <span matSuffix class="autocomplete-spinner">
            @if (loadingDestination()) {
              <mat-spinner diameter="20" strokeWidth="3"></mat-spinner>
            }
          </span>

          <mat-autocomplete requireSelection #deliveryAuto="matAutocomplete">
            @for (option of filteredDestinationOptions; track option.id) {
              <mat-option
                [value]="formatLocation(option)"
                (onSelectionChange)="onLocationSelected(option, 'destination')"
              >
                {{ formatLocation(option) }}
              </mat-option>
            }
          </mat-autocomplete>

          @if (form.get('destination')?.hasError('required') && form.get('destination')?.touched) {
            <mat-error>Destination is required</mat-error>
          }
        </mat-form-field>

      </div>
    </div>

    <div class="status-row">
      <mat-checkbox formControlName="self_load">
        Self load
      </mat-checkbox>

      <div class="distance-row">
        @if (distance()) {
          {{ distance() | number }} mi
          <button mat-icon-button type="button" (click)="openGoogleMaps()">
            <mat-icon>map</mat-icon>
          </button>
        }
      </div>

      <mat-checkbox formControlName="self_unload">
        Self unload
      </mat-checkbox>
    </div>

    <!-- <mat-checkbox formControlName="roundtrip">
      Roundtrip
    </mat-checkbox> -->

    <mat-form-field appearance="outline" class="medium-field">
      <mat-label>Commodity</mat-label>
      <input matInput formControlName="commodity" placeholder="Commodity" />
      @if (form.get('commodity')?.invalid) {
        <mat-error>Commodity is required</mat-error>
      }
    </mat-form-field>

    <mat-form-field appearance="outline" class="small-field">
      <mat-label>Weight (lbs)</mat-label>
      <input matInput formControlName="weight" type="number" />
    </mat-form-field>

    <mat-form-field appearance="outline" class="small-field">
      <mat-label>Length (ft)</mat-label>
      <input matInput formControlName="length" type="number" step="1" />
      @if (form.get('length')?.value) {
        <mat-hint>{{ toFeetAndInchesFromFeet(form.get('length')?.value) }}</mat-hint>
      }
    </mat-form-field>

    <mat-form-field appearance="outline" class="small-field">
      <mat-label>Width (ft)</mat-label>
      <input matInput formControlName="width" type="number" step="1" />

      <button matTextSuffix mat-button type="button" class="blue-btn" (click)="form.get('width')?.setValue(8.5)">
        Legal
      </button>

      <mat-hint>
        {{ form.get('width')?.value ? toFeetAndInchesFromFeet(form.get('width')?.value) : 'Legal = 8 ft 6 in' }}
      </mat-hint>
    </mat-form-field>

    <mat-form-field appearance="outline" class="small-field">
      <mat-label>Height (ft)</mat-label>
      <input matInput formControlName="height" type="number" step="1" />

      <button matTextSuffix mat-button type="button" class="blue-btn" (click)="form.get('height')?.setValue(11.5)">
        Legal
      </button>

      <mat-hint>
        {{ form.get('height')?.value ? toFeetAndInchesFromFeet(form.get('height')?.value) : 'Legal = 11 ft 6 in' }}
      </mat-hint>
    </mat-form-field>

    <mat-form-field appearance="outline" class="small-field">
      <mat-label>Well length (ft)</mat-label>
      <input matInput formControlName="well_length" type="number" step="1" />
      @if (form.get('well_length')?.value) {
        <mat-hint>{{ toFeetAndInchesFromFeet(form.get('well_length')?.value) }}</mat-hint>
      }
    </mat-form-field>

    <mat-form-field appearance="outline" class="small-field">
      <mat-label>Operable</mat-label>
      <mat-select formControlName="operable">
        @for (option of operableOptions; track option.value) {
          <mat-option [value]="option.value">
            {{ option.name }}
          </mat-option>
        }
      </mat-select>
    </mat-form-field>

    <mat-form-field appearance="outline" class="small-field">
      <mat-label>Divisibility</mat-label>
      <mat-select formControlName="divisible">
        @for (option of divisibleOptions; track option.value) {
          <mat-option [value]="option.value">
            {{ option.name }}
          </mat-option>
        }
      </mat-select>
    </mat-form-field>

    <mat-form-field appearance="outline" class="small-field">
      <mat-label>Rate</mat-label>
      <span matTextPrefix>$&nbsp;&nbsp;</span>
      <input matInput formControlName="rate" type="number" step="25" (change)="onRateChange()" />
    </mat-form-field>

    <div class="payment-options-row">
      <mat-checkbox formControlName="cod">
        COD
      </mat-checkbox>

      <mat-checkbox formControlName="permits_reimbursed">
        Permits Reimbursed
      </mat-checkbox>
    </div>

    <mat-form-field appearance="outline" class="notes-field">
      <mat-label>Load notes</mat-label>
      <textarea matInput formControlName="note"></textarea>
    </mat-form-field>

    <mat-form-field appearance="outline" class="medium-field">
      <mat-label>Reference</mat-label>
      <input matInput formControlName="reference" />
    </mat-form-field>

    @if (carriers.length > 1) {
      <mat-form-field appearance="outline" class="medium-field">
        <mat-label>Carrier</mat-label>

        <mat-select formControlName="carrier_id" (selectionChange)="onCarrierChange($event)">
          @for (carrier of carriers; track carrier.id) {
            <mat-option [value]="carrier.id">
              {{ carrier.name }}
            </mat-option>
          }
        </mat-select>
      </mat-form-field>
    } @else if (carriers.length === 1) {
      <input type="hidden" formControlName="carrier_id" />
    }

    <div class="checkbox-row">
      <mat-checkbox formControlName="military_base_access">Military</mat-checkbox>
      <mat-checkbox formControlName="twic">TWIC</mat-checkbox>
      <mat-checkbox formControlName="emodal">E-Modal</mat-checkbox>
      <mat-checkbox formControlName="outriggers">Outriggers</mat-checkbox>
      <mat-checkbox formControlName="tarp">Tarp</mat-checkbox>
      <mat-checkbox formControlName="hazmat">Hazmat</mat-checkbox>
    </div>

  </form>
</mat-dialog-content>

<mat-dialog-actions align="end">
  @if (mode === 'edit' && load) {
    <button mat-button type="button" class="danger-button" (click)="deleteLoad()">
      Delete
    </button>
  }

  <button mat-button type="button" (click)="onCancel()">Close</button>

  <button 
    mat-button 
    type="button" 
    class="lightgreen-button" 
    (click)="onSubmit()"
    [disabled]="form.invalid"
  >
    {{ mode === 'add' ? 'Add' : 'Update' }}
  </button>
</mat-dialog-actions>